import React, { useEffect, useState } from "react";
import { addSalary, deleteSalary, getSalaries, getSubjects, getTeachers, updateSalary } from "../services/api";
import DateInput from "../components/DateInput";
import { confirmAppAction, showAppToast } from "../utils/feedback";
import { formatDate, toInputDate } from "../utils/helpers";
import "./FinancePage.css";

const emptySalaryForm = {
  teacher_id: "",
  payPeriod: "",
  amount: "",
  paidDate: "",
  status: "unpaid",
  notes: ""
};

function SalaryPage() {
  const [salaries, setSalaries] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [formData, setFormData] = useState(emptySalaryForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchTeachers();
    fetchSubjects();
  }, []);

  useEffect(() => {
    fetchTeachers();
    fetchSalaries();
  }, [selectedSubject, selectedStatus]);

  const fetchTeachers = async () => {
    const res = await getTeachers(selectedSubject, { fields: "lookup" });
    setTeachers(res.data);
  };

  const fetchSubjects = async () => {
    const res = await getSubjects();
    setSubjects(res.data);
  };

  const fetchSalaries = async () => {
    const res = await getSalaries({
      subject: selectedSubject,
      status: selectedStatus
    });
    setSalaries(res.data);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData(emptySalaryForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSaveSalary = async () => {
    if (!formData.teacher_id || !formData.payPeriod || !formData.amount) {
      showAppToast({
        type: "error",
        title: "Missing salary details",
        message: "Please select a teacher, pay period, and amount before saving."
      });
      return;
    }

    const payload = {
      ...formData,
      amount: Number(formData.amount)
    };

    try {
      if (editingId) {
        await updateSalary(editingId, payload);
        showAppToast({
          type: "success",
          title: "Salary record updated",
          message: "The salary payment details were updated successfully."
        });
      } else {
        await addSalary(payload);
        showAppToast({
          type: "success",
          title: "Salary record added",
          message: "The salary record was created successfully."
        });
      }

      resetForm();
      fetchSalaries();
    } catch (err) {
      showAppToast({
        type: "error",
        title: "Failed to save salary record",
        message: err.response?.data?.error || err.message
      });
    }
  };

  const handleEdit = (salary) => {
    setFormData({
      teacher_id: salary.teacher_id,
      payPeriod: salary.payPeriod || "",
      amount: String(salary.amount),
      paidDate: toInputDate(salary.paidDate),
      status: salary.status,
      notes: salary.notes || ""
    });
    setEditingId(salary._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmAppAction({
      title: "Delete salary record",
      message: "This salary record will be removed permanently.",
      confirmLabel: "Delete"
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteSalary(id);
      showAppToast({
        type: "success",
        title: "Salary record deleted",
        message: "The salary record was removed successfully."
      });
      fetchSalaries();
    } catch (err) {
      showAppToast({
        type: "error",
        title: "Failed to delete salary record",
        message: err.response?.data?.error || err.message
      });
    }
  };

  const paidCount = salaries.filter((salary) => salary.status === "paid").length;
  const pendingCount = salaries.filter((salary) => salary.status === "unpaid").length;
  const totalAmount = salaries.reduce((sum, salary) => sum + Number(salary.amount || 0), 0);

  return (
    <div className="finance-page">
      {/* <div className="finance-page__header">
        <h1>Salary Management</h1>
        <p>Manage teacher salary periods, payment status, and payout records in one place.</p>
      </div> */}

      <div className="finance-page__stats">
        <div className="finance-page__stat-card">
          <h3>Total Records</h3>
          <p>{salaries.length}</p>
        </div>
        <div className="finance-page__stat-card">
          <h3>Paid</h3>
          <p>{paidCount}</p>
        </div>
        <div className="finance-page__stat-card">
          <h3>Pending</h3>
          <p>{pendingCount}</p>
        </div>
        <div className="finance-page__stat-card">
          <h3>Total Amount</h3>
          <p>{totalAmount}</p>
        </div>
      </div>

      <div className="finance-page__filters">
        <div className="finance-page__filter-card">
          <label>Subject</label>
          <select value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)}>
            <option value="all">All Subjects</option>
            {subjects.map((subject) => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>
        </div>
        <div className="finance-page__filter-card">
          <label>Status</label>
          <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </div>
      </div>

      <div className="finance-page__panel">
        {showForm && (
          <div className="finance-page__form">
            <h2>{editingId ? "Edit Salary Record" : "Add Salary Record"}</h2>
            <div className="finance-page__form-grid">
              <div>
                <label>Teacher</label>
                <select name="teacher_id" value={formData.teacher_id} onChange={handleInputChange}>
                  <option value="">Select Teacher</option>
                  {teachers.map((teacher) => (
                    <option key={teacher._id} value={teacher._id}>
                      {teacher.name} ({teacher.subject})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>Pay Period</label>
                <input name="payPeriod" type="month" value={formData.payPeriod} onChange={handleInputChange} />
              </div>
              <div>
                <label>Amount</label>
                <input name="amount" type="number" value={formData.amount} onChange={handleInputChange} />
              </div>
              <div>
                <label>Paid Date</label>
                <DateInput name="paidDate" value={formData.paidDate} onChange={handleInputChange} />
              </div>
              <div>
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div className="finance-page__form-grid-full">
                <label>Notes</label>
                <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="3" />
              </div>
            </div>
            <div className="finance-page__form-actions">
              <button className="finance-page__primary-btn" onClick={handleSaveSalary}>
                {editingId ? "Update Salary Record" : "Save Salary Record"}
              </button>
              <button className="finance-page__secondary-btn" onClick={resetForm}>Cancel</button>
            </div>
          </div>
        )}

        {!showForm && (
          <button className="finance-page__primary-btn" onClick={() => setShowForm(true)}>
            Add Salary Record
          </button>
        )}

        <div className="finance-page__table-wrap">
          <table className="finance-page__table">
            <thead>
              <tr>
                <th>Teacher</th>
                <th>Subject</th>
                <th>Pay Period</th>
                <th>Amount</th>
                <th>Paid Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {salaries.map((salary) => (
                <tr key={salary._id}>
                  <td>{salary.teacher_name}</td>
                  <td>{salary.subject}</td>
                  <td>{salary.payPeriod}</td>
                  <td>{salary.amount}</td>
                  <td>{formatDate(salary.paidDate)}</td>
                  <td>
                    <span className={`finance-page__badge finance-page__badge--${salary.status}`}>{salary.status}</span>
                  </td>
                  <td>
                    <button className="finance-page__inline-btn" onClick={() => handleEdit(salary)}>Edit</button>
                    <button className="finance-page__inline-btn finance-page__inline-btn--danger" onClick={() => handleDelete(salary._id)}>Delete</button>
                  </td>
                </tr>
              ))}
              {salaries.length === 0 && (
                <tr>
                  <td colSpan="7" className="finance-page__empty">No salary records found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default SalaryPage;
